import React from 'react';

import { type PatientImportEditableField } from '@eluve/api-contract';
import { Button, Caption, Icon, Stack, cn, tv } from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { useImportRowMutations } from '../hooks/useImportRowMutations';
import { displayValueForField } from '../utils';

import { FIELDS, FieldCell, type ImportRow, ResolvedPill } from './RowCard';

const memberDimStyles = tv({
  variants: {
    state: {
      skipped: 'opacity-50',
      resolved: 'opacity-75',
      active: '',
    },
  },
  defaultVariants: { state: 'active' },
});

const GroupMember: React.FC<{
  row: ImportRow;
  label: string;
  isLast: boolean;
  onRemove: () => void;
  onUpdateField: (
    field: PatientImportEditableField,
    next: string | null,
  ) => Promise<unknown>;
}> = ({ row, label, isLast, onRemove, onUpdateField }) => {
  const resolved = row.resolution !== null;
  const isSkipped = row.resolution === 'SKIPPED';
  const isRemoved = row.resolution === 'REMOVED';
  const dimClass = memberDimStyles({
    state: isSkipped ? 'skipped' : resolved ? 'resolved' : 'active',
  });
  const strikeClass = isSkipped || isRemoved ? 'line-through' : '';

  return (
    <div
      className={cn(
        'px-4 py-3',
        !isLast && 'border-b border-dashed border-border-tertiary',
      )}
    >
      <Stack gap={3}>
        <Stack direction="horizontal" justify="between" align="start" gap={3}>
          <Stack
            direction="horizontal"
            gap={2}
            align="center"
            className="min-w-0 flex-wrap"
          >
            <Caption color="tertiary" size="2xs">
              {label}
            </Caption>
            <Caption color="tertiary" size="2xs" className="font-mono">
              Row {row.rowNumber}
            </Caption>
            {row.resolution ? (
              <ResolvedPill resolution={row.resolution} />
            ) : null}
          </Stack>
          {resolved ? null : (
            <Button size="sm" variant="outline" onClick={onRemove}>
              <Icon name="Trash" size="auto" />
              Remove this row
            </Button>
          )}
        </Stack>

        <div className={cn('grid grid-cols-6 gap-2', dimClass, strikeClass)}>
          {FIELDS.map(({ key, field, label: fieldLabel }) => (
            <FieldCell
              key={field}
              label={fieldLabel}
              value={displayValueForField(
                field,
                (row[key] as string | null) ?? null,
              )}
              blocking={false}
              onSave={async (next) => {
                await onUpdateField(field, next);
              }}
            />
          ))}
        </div>
      </Stack>
    </div>
  );
};

// One in-file duplicate clique (size >= 2). The backend auto-promotes the last
// unresolved member to FIXED once the rest are REMOVED/SKIPPED, so any action
// that leaves exactly one survivor resolves the whole group (ENG-4588).
export const DupeGroupCard: React.FC<{
  rows: ImportRow[];
  tenantId: string;
  jobId: string;
  onMutationSuccess?: () => void | Promise<void>;
}> = ({ rows, tenantId, jobId, onMutationSuccess }) => {
  const { resolveRow, resolveBulk, unresolveBulk, updateField } =
    useImportRowMutations(tenantId, jobId, onMutationSuccess);

  const members = [...rows].sort((a, b) => a.rowNumber - b.rowNumber);
  const firstRowNumber = members[0]?.rowNumber;
  const unresolved = members.filter((r) => r.resolution === null);
  const allResolved = unresolved.length === 0;

  const removeOne = (row: ImportRow) => {
    void resolveRow(row.id, 'REMOVED');
  };
  const skipGroup = () => {
    void resolveBulk(
      unresolved.map((r) => r.id),
      'SKIPPED',
    );
  };
  const undoGroup = () => {
    void unresolveBulk(members.map((r) => r.id));
  };

  return (
    <div
      className={cn(
        'relative border-b border-border-tertiary last:border-b-0',
        allResolved ? 'bg-bg-positive-subtle' : 'bg-bg-primary',
      )}
    >
      <div
        className={cn(
          'absolute inset-y-2 left-0 w-1 rounded-r-sm',
          allResolved ? 'bg-bg-positive-strong-primary' : 'bg-border-tertiary',
        )}
        aria-hidden
      />
      <Stack
        direction="horizontal"
        justify="between"
        align="center"
        gap={3}
        className="px-4 pt-3"
      >
        <Caption color="tertiary" size="2xs">
          {pluralize(members.length, 'identical row')}
        </Caption>
        {allResolved ? (
          <Button size="sm" variant="ghost" onClick={undoGroup}>
            <Icon name="RefreshCw" size="auto" />
            Undo
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={skipGroup}>
            <Icon name="X" size="auto" />
            Skip group
          </Button>
        )}
      </Stack>
      {members.map((row, index) => (
        <GroupMember
          key={row.id}
          row={row}
          label={
            index === 0
              ? 'First occurrence'
              : `Duplicate of row ${firstRowNumber ?? row.rowNumber}`
          }
          isLast={index === members.length - 1}
          onRemove={() => removeOne(row)}
          onUpdateField={(field, next) => updateField(row.id, field, next)}
        />
      ))}
    </div>
  );
};
